import { BoxProps } from '@mui/material';
import Box from '@mui/material/Box';
import clsx from 'clsx';

import { SpacingValue } from './_types';
import { resolveValue } from './_utils';
import { CssVarDefinitions } from '../../theme/types';
import { negate } from '../../utils';
import { classNamesFor } from '../../utils/classNames';
import { insetVars, mergeStyles } from '../variables';

const classNamesFactory = classNamesFor('Bleed');

const classNames = {
  root: classNamesFactory.root(),
};

type Margin = SpacingValue;

export type BleedProps = Omit<
  BoxProps,
  | 'mx'
  | 'my'
  | 'mt'
  | 'mr'
  | 'mb'
  | 'ml'
  | 'marginTop'
  | 'marginRight'
  | 'marginBottom'
  | 'marginLeft'
> & {
  /** Alias for `marginLeft` */
  ml?: Margin;
  /** Alias for `marginRight` */
  mr?: Margin;
  /** Sets the same value for `marginLeft` and `marginRight` */
  mx?: Margin;
  /** Alias for `marginTop` */
  mt?: Margin;
  /** Alias for `marginBottom` */
  mb?: Margin;
  /** Sets the same value for `marginTop` and `marginBottom` */
  my?: Margin;
  /**
   * Sets `marginTop`
   * `true` - inherits from `insetVars.refs.pt` (negated)
   * `false` - no value
   * concrete number/string - sets to concrete value (negated)
   */
  marginTop?: Margin;
  /**
   * Sets `marginRight`
   * `true` - inherits from `insetVars.refs.pr` (negated)
   * `false` - no value
   * concrete number/string - sets to concrete value (negated)
   */
  marginRight?: Margin;
  /**
   * Sets `marginBottom`
   * `true` - inherits from `insetVars.refs.pb` (negated)
   * `false` - no value
   * concrete number/string - sets to concrete value (negated)
   */
  marginBottom?: Margin;
  /**
   * Sets `marginLeft`
   * `true` - inherits from `insetVars.refs.pl` (negated)
   * `false` - no value
   * concrete number/string - sets to concrete value (negated)
   */
  marginLeft?: Margin;
  /** CSS var definitions will be added to style attribute */
  cssVars?: CssVarDefinitions;
};

/**
 * Box with negative margins applied to all sides or individual sides.
 * Margin value is taken from `--inset-[pt|pr|pb|pl]` css variable.
 * Opposite of Inset - it 'bleeds' outside the parent's padding.
 * By default, it bleeds (applies negative margins) horizontally.
 * If the margin value is `true`, its value will be inherited from the ancestor, via CSS variable
 * If the margin value is `false`, it will be zero.
 * Bleed is useful when we need to display only a certain section (or sections) of the Inset, taking a full width.
 * @see `./Inset.tsx`
 */
export const Bleed = ({
  children,
  mx = true,
  my = false,
  mt,
  mr,
  mb,
  ml,
  marginTop,
  marginRight,
  marginBottom,
  marginLeft,
  cssVars,
  style,
  className,
  ...rest
}: BleedProps) => {
  return (
    <Box
      className={clsx(classNames.root, className)}
      marginTop={resolveValue({
        value: negate(insetVars.refs.pt),
        directional: my,
        concrete: marginTop ?? mt,
      })}
      marginRight={resolveValue({
        value: negate(insetVars.refs.pr),
        directional: mx,
        concrete: marginRight ?? mr,
      })}
      marginBottom={resolveValue({
        value: negate(insetVars.refs.pb),
        directional: my,
        concrete: marginBottom ?? mb,
      })}
      marginLeft={resolveValue({
        value: negate(insetVars.refs.pl),
        directional: mx,
        concrete: marginLeft ?? ml,
      })}
      style={mergeStyles(style, cssVars)}
      {...rest}
    >
      {children}
    </Box>
  );
};

Bleed.classNames = classNames;
